/**
 * Translates the legacy flex-layout (`@ngbracket/ngx-layout`) vocabulary that the
 * server contract still speaks — `layout` direction strings, `"main-axis cross-axis"`
 * alignment strings, and fxFlex-style item sizing — into plain CSS flexbox styles
 * for a `[style]` binding.
 *
 * The mapping is ported from the library's own style builders (`buildLayoutCSS`
 * in `_private-utils` and `LayoutAlignStyleBuilder.buildStyles` in `flex`) so a
 * translated container renders identically to one using the `fxLayout` /
 * `fxLayoutAlign` directives it replaces. Library quirks are intentionally
 * preserved:
 *
 * - An unrecognized direction falls back to `'row'` (NOT `'column'` — component
 *   defaults are the caller's concern).
 * - Any unrecognized wrap token (e.g. `'row wrap-x'`) still becomes `flex-wrap: wrap`.
 * - An explicit `stretch` cross-axis adds `max-height: 100%` (row flows) or
 *   `max-width: 100%` (column flows) so children can fill the cross axis.
 *
 * This is pure translation plumbing: it deletes when the server contract goes
 * CSS-native (tracked in the modernization playbook's function-patch table).
 */

/**
 * The flex directions the `layout` vocabulary accepts, in library order.
 */
const LAYOUT_DIRECTIONS = ['row', 'column', 'row-reverse', 'column-reverse'] as const;

/**
 * Builds the complete inline style object for a flex container from the
 * legacy `layout` and `layoutAlign` token strings.
 *
 * @param layout - a direction token, optionally followed by a wrap token and/or
 *                 `inline` (e.g. `'column'`, `'row wrap'`, `'row inline'`)
 * @param layoutAlign - a `"main-axis cross-axis"` alignment string
 *                      (e.g. `'space-between center'`); either token may be omitted
 */
export function flexContainerStyle(layout: string, layoutAlign: string): Record<string, string> {
  const { direction, wrap, inline } = parseLayout(layout);

  const css: Record<string, string> = {
    display: inline ? 'inline-flex' : 'flex',
    'box-sizing': 'border-box',
    'flex-direction': direction,
  };
  if (wrap) {
    css['flex-wrap'] = wrap;
  }

  const [mainAxis, crossAxis] = (layoutAlign ?? '').split(' ');

  // main axis
  switch (mainAxis) {
    case 'center':
    case 'space-around':
    case 'space-between':
    case 'space-evenly':
      css['justify-content'] = mainAxis;
      break;
    case 'end':
    case 'flex-end':
      css['justify-content'] = 'flex-end';
      break;
    case 'start':
    case 'flex-start':
    default:
      css['justify-content'] = 'flex-start';
      break;
  }

  // cross axis
  switch (crossAxis) {
    case 'start':
    case 'flex-start':
      css['align-items'] = css['align-content'] = 'flex-start';
      break;
    case 'center':
      css['align-items'] = css['align-content'] = 'center';
      break;
    case 'end':
    case 'flex-end':
      css['align-items'] = css['align-content'] = 'flex-end';
      break;
    case 'space-between':
    case 'space-around':
      css['align-content'] = crossAxis;
      css['align-items'] = 'stretch';
      break;
    case 'baseline':
      css['align-content'] = 'stretch';
      css['align-items'] = 'baseline';
      break;
    case 'stretch':
      css['align-items'] = css['align-content'] = 'stretch';
      css[direction.startsWith('row') ? 'max-height' : 'max-width'] = '100%';
      break;
    default:
      css['align-items'] = css['align-content'] = 'stretch';
      break;
  }

  return css;
}

/**
 * Builds the inline style for a flex item from structured `grow` / `shrink` /
 * `basis` values: the `flex` shorthand plus the min/max-width a pixel basis
 * locks — both when the box can neither grow nor shrink, `min` when it can only
 * grow, `max` when it can only shrink. Without these, `flex` alone leaves the
 * basis a soft hint that wide content overflows past.
 *
 * Ported from fxFlex's `FlexStyleBuilder` for the row-direction cases this app
 * uses (a column-direction item would lock heights instead — extend if a
 * consumer ever needs that).
 *
 * @param item - the sizing values; omitted fields use fxFlex's `0 0 auto`
 */
export function flexItemStyle({
  grow = 0,
  shrink = 0,
  basis = 'auto',
}: {
  grow?: number;
  shrink?: number;
  basis?: string;
}): Record<string, string> {
  const style: Record<string, string> = { flex: `${grow} ${shrink} ${basis}` };

  if (basis !== 'auto' && basis !== '' && basis !== '0%' && basis !== '0px') {
    if (!(grow === 0 && shrink > 0)) {
      style['min-width'] = basis;
    }
    if (!(grow > 0 && shrink === 0)) {
      style['max-width'] = basis;
    }
  }

  return style;
}

/**
 * Splits a `layout` token string into its validated parts.
 */
function parseLayout(layout: string): { direction: string; wrap: string; inline: boolean } {
  const [first = '', second = '', third = ''] = (layout ?? '').toLowerCase().split(' ');

  const direction = (LAYOUT_DIRECTIONS as readonly string[]).includes(first) ? first : LAYOUT_DIRECTIONS[0];

  // `inline` may take the wrap token's position or follow it
  const inline = second === 'inline' || third === 'inline';
  const wrapToken = second === 'inline' ? (third === 'inline' ? '' : third) : second;

  return { direction, wrap: normalizeWrap(wrapToken), inline };
}

/**
 * Converts a wrap token to its `flex-wrap` value; every unrecognized non-empty
 * token means `'wrap'`, matching the library.
 */
function normalizeWrap(wrap: string): string {
  if (!wrap || wrap === 'inline') {
    return '';
  }
  switch (wrap) {
    case 'reverse':
    case 'wrap-reverse':
    case 'reverse-wrap':
      return 'wrap-reverse';
    case 'no':
    case 'none':
    case 'nowrap':
      return 'nowrap';
    default:
      return 'wrap';
  }
}
